/**
 * Daily Report Page
 *
 * The brand's trading table: one row per day, ISO week (Mon–Sun) or calendar
 * month, with orders, product mix, revenue, acquisition counts, AOV and CAC.
 *
 * The table itself is components/DailyReportTable, shared with the dashboard's
 * 7-day card. This file is the controls around it — range, granularity, and the
 * caveats that belong to the full report rather than to any table showing it.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { format, parse, subDays } from 'date-fns'
import { RefreshCw, Loader2, AlertCircle, CalendarDays } from 'lucide-react'

import { dailyReportAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { Card, CardContent, Button } from '../components/ui'
import DailyReportTable, { formatMoney, formatCount } from '../components/DailyReportTable'
import { DateRangePicker } from '../components/DateRangePicker'
import NoAdSpendNotice from '../components/NoAdSpendNotice'

const GRANULARITIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
]

function getDefaultDateRange() {
  return { from: subDays(new Date(), 29), to: new Date() }
}

export default function DailyReportPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { currentClinicId } = useAuthStore()

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const granularity = searchParams.get('granularity') || 'daily'

  const dateRange = useMemo(() => {
    const startParam = searchParams.get('start')
    const endParam = searchParams.get('end')
    if (startParam && endParam) {
      const from = parse(startParam, 'yyyy-MM-dd', new Date())
      const to = parse(endParam, 'yyyy-MM-dd', new Date())
      if (!isNaN(from.getTime()) && !isNaN(to.getTime())) return { from, to }
    }
    return getDefaultDateRange()
  }, [searchParams])

  const setDateRange = useCallback((newRange) => {
    if (!newRange?.from || !newRange?.to) return
    setSearchParams(prev => {
      const params = new URLSearchParams(prev)
      params.set('start', format(newRange.from, 'yyyy-MM-dd'))
      params.set('end', format(newRange.to, 'yyyy-MM-dd'))
      return params
    })
  }, [setSearchParams])

  const setGranularity = useCallback((value) => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev)
      params.set('granularity', value)
      return params
    })
  }, [setSearchParams])

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await dailyReportAPI.get({
        startDate: format(dateRange.from, 'yyyy-MM-dd'),
        endDate: format(dateRange.to, 'yyyy-MM-dd'),
        granularity,
      })
      if (response.data.success) setData(response.data.data)
    } catch (err) {
      console.error('[DAILY-REPORT] Error:', err)
      setError(err.response?.data?.error?.message || err.message)
      setData(null)
    } finally {
      setLoading(false)
      setIsRefreshing(false)
    }
  }, [dateRange, granularity])

  useEffect(() => { fetchData() }, [fetchData, currentClinicId])

  const handleRefresh = () => {
    setIsRefreshing(true)
    fetchData()
  }

  // Every period in the selected range is rendered, including the ones that
  // never traded. A zero Tuesday is a fact about the week, and trimming empty
  // rows would silently return a different range than the one that was asked
  // for — the picker is the only thing that decides what is on screen.
  //
  // Newest first, reversed HERE rather than in the API: the endpoint returns a
  // time series, and ascending is the order anything that charts or diffs one
  // wants. This is the table's reading order — today at the top, where you look
  // first — and it is the table's business alone.
  const rows = useMemo(() => [...(data?.rows || [])].reverse(), [data])
  const totals = data?.totals

  const unattributed = totals?.unattributed_orders || 0
  const creditSettled = data?.credit_settled || { orders: 0, amount: 0 }

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold truncate">Daily Report</h1>
          <p className="text-xs text-muted-foreground hidden sm:block">
            Orders, revenue, AOV and CAC for {data?.displayName || 'this clinic'}
            {data?.timezone && ` · days run on ${data.timezone.split('/')[1].replace('_', ' ')} time`}
          </p>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <div className="flex rounded-lg border border-input bg-background p-1">
            {GRANULARITIES.map(g => (
              <button
                key={g.value}
                onClick={() => setGranularity(g.value)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  granularity === g.value
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                {g.label}
              </button>
            ))}
          </div>
          <DateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} />
          <Button
            variant="outline"
            size="icon"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="h-8 w-8 sm:h-9 sm:w-9"
          >
            {isRefreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      <NoAdSpendNotice />

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <Card className="border-0 bg-transparent shadow-none md:border md:bg-card md:shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <CalendarDays className="h-8 w-8 mb-2 opacity-40" />
              <p className="text-sm">No charges in this range.</p>
            </div>
          ) : (
            <DailyReportTable
              rows={rows}
              totals={totals}
              granularity={granularity}
              currentPeriodKey={data?.current_period_key}
              clinic={data?.clinic}
            />
          )}
        </CardContent>
      </Card>

      {/* The two places this table can silently mislead, said out loud. */}
      <div className="text-xs text-muted-foreground space-y-1">
        <p>
          One charge is one order. A prepaid multi-month bundle is the single charge that
          bought it, not its individual fills, so Total Revenue ÷ Total Orders is what a
          customer actually paid. Free renewals carry no charge and are not counted — this is
          the only place the P&amp;L's Orders Placed runs higher.
        </p>
        <p>
          Sales columns count new customers only, attributed by the price book stamped on the
          patient at conversion. Reddit + Sales Team can be less than Total Sales: a patient
          who arrived through a funnel that sets no price book is counted only in the total.
        </p>
        <p>
          <strong>Rebills</strong> are second-or-later bills on a subscription whose first bill
          went through. Every other charge from an existing customer is a{' '}
          <strong>Plan Change</strong>: a new plan or dose, a purchase redone at checkout, or an
          add-on beside a running plan. Plans swapped using store credit are in neither.
        </p>
        <p>
          <strong>Refunds</strong> are dated on the day the money went back, not the day of the
          sale. Net Revenue is Total Revenue less those refunds.{' '}
          {data?.refunds?.checked === false
            ? 'Refunds cannot be read for this clinic (only clinics billed through Manifold), so those columns are blank.'
            : 'A charge refunded in full within a day is left out of the sales entirely, so it is not counted as a refund either.'}
        </p>
        {unattributed > 0 && (
          <p>
            {formatCount(unattributed)} {unattributed === 1 ? 'charge' : 'charges'} carrying{' '}
            {formatMoney(totals.unattributed_revenue)} could not be told apart as new or
            recurring — a bundle with no fill written yet, or a payment whose order never
            generated. They are in Total Orders and Total Revenue but in neither split.
          </p>
        )}
        {creditSettled.orders > 0 && (
          <p>
            {formatCount(creditSettled.orders)}{' '}
            {creditSettled.orders === 1 ? 'shipment' : 'shipments'} worth{' '}
            {formatMoney(creditSettled.amount)} were paid from customer balance rather
            than a card, and are in none of the figures above. The cash was collected
            when the credit was issued, so counting it again here would book the same
            dollar twice.
          </p>
        )}
        <p>
          Leads are people who gave the funnel their contact details, counted on the day they
          first did. Lead CVR is the same period's Total Sales over its Leads — a same-day
          ratio, not a cohort, so a lead who buys a week later is counted in one row's sales
          and an earlier row's leads. On a heavy ad day it reads low for that reason alone.
        </p>
        <p>
          <strong>Sales</strong> and <strong>Attributed Sales</strong> are the same sales
          counted twice, once each way, so both bands sum to Total Sales. Sales asks which
          checkout took the money — the price book the funnel set, Reddit or Sales Team, the
          Customer.io win-back link (Email or SMS, by which message was tapped), and{' '}
          <em>Web</em> where none applied. Attributed Sales asks which source first brought
          the buyer in. A customer a Meta ad found and an agent closed is one Sales Team sale
          and one Meta attributed sale, and neither statement contradicts the other.
        </p>
        <p>
          <strong>Leads and Attributed Sales are both first touch.</strong> Attributed Sales is
          the Channels report&apos;s First Touch model: the buyer&apos;s earliest visit in the 30
          days before paying, including paid ad clicks BigQuery recorded that core missed, so
          the two reports give the same numbers. A lead belongs to the
          channel that found the person, so the price book is not consulted for one — the
          agents close leads the ads bought, and crediting them the lead as well would
          measure their conversion against leads they never generated. Neither register works
          alone on either side: the Reddit funnel sets a price book but sends no UTM and is
          recognisable only by its landing page, while TMATES sets no price book on anyone
          and the UTM does all the work.
        </p>
        <p>
          The two CAC columns are a bracket rather than two attempts at one number.
          <strong> Ad Spend CAC</strong> divides the budget across the sales ads could
          plausibly have produced — Meta, Google, Sales Team and Other. Other is included
          deliberately: an ad click whose UTM was stripped by the browser, and a patient who
          saw the ad and typed the address a day later, are indistinguishable from a walk-in
          once they land there, and excluding them made the figure an upper bound rather
          than a measurement. <strong>Total CAC</strong> divides the same spend across every new
          customer, organic ones included, and is the lower bound.
        </p>
        <p>
          <strong>Reddit is the only channel outside the Ad Spend CAC denominator</strong> —
          a funnel of its own with no ad account behind it, so a Reddit sale is one the
          budget did not cause. Only Science.co sets a Reddit price book, so that column
          appears there and nowhere else. <strong>Sales Team is inside</strong> the
          denominator, even though the phone room is people rather than an ad account: the
          agents work leads the ads brought in, and the price book they stamp at close
          records who finished the sale, not who found the customer.
        </p>
        <p>
          Where a brand spends on a channel that sends no click and writes no source at all
          — MDExam's CTV is about a third of its budget — that spend sits in the numerator
          with sales it cannot be told apart from, so the two CAC figures move closer
          together and Total CAC is the safer read for that brand.
        </p>
        <p>
          <strong>Other</strong> is not a synonym for direct. It holds genuine direct arrivals,
          the sources we do not bucket (Customer.io, Katalys, ChatGPT, Trustpilot), and
          everyone who carries no source at all — about a fifth of TMATES patients. A channel
          with no leads in a period shows a dash rather than 0%, and a channel a brand does not
          run is dropped from the table entirely rather than shown as a column of dashes.
        </p>
        {data && !data.ad_spend_available && (
          <p>
            No ad spend has been reported for this brand in the selected range, so both CAC
            columns are blank rather than zero.
          </p>
        )}
        {data && !data.leads_available && (
          <p>
            This brand's funnel keeps no lead history, so Leads and Lead CVR are blank rather
            than zero — nobody has recorded a lead here, which is not the same as nobody
            arriving.
          </p>
        )}
      </div>
    </div>
  )
}
